<script setup lang="ts">
  import { computed, onMounted, ref, useId } from 'vue';
  import Loader from '../../Loader/Loader.vue';
  import { LoaderSize } from '../../Loader/Loader.types';
  import { FieldSize, FormField } from '../FormField';
  import { helperTextClass } from '../FormField/FormField.css';
  import {
    fieldWrapperClass,
    loaderWrapperClass,
    textareaClass,
    textareaSmallClass,
  } from './TextArea.css';
  import type { TextAreaEmits, TextAreaProps } from './TextArea.types';

  const props = withDefaults(defineProps<TextAreaProps>(), {
    optionalLabel: false,
    disabled: false,
    required: false,
    loading: false,
    autofocus: false,
    rows: 4,
    showCounter: false,
    resize: 'vertical',
    size: FieldSize.MD,
  });

  const emit = defineEmits<TextAreaEmits>();

  const uid = useId();
  const inputId = computed(() => props.id ?? uid);
  const resolvedTestId = computed(() => props.testId ?? `ds-text-area-${uid}`);

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const textareaStyle = computed(() => ({
    resize: props.resize,
    minHeight: props.minHeight,
  }));

  const textareaRef = ref<HTMLTextAreaElement | null>(null);

  const hasCounter = computed(() => props.showCounter && !!props.maxLength);
  const counterText = computed(
    () => `${(props.modelValue ?? '').length}/${props.maxLength}`,
  );

  const onInput = (event: Event) => {
    emit('update:modelValue', (event.target as HTMLTextAreaElement).value);
  };

  const onBlur = (event: FocusEvent) => {
    emit('blur', event);
  };

  onMounted(() => {
    if (props.autofocus) {
      textareaRef.value?.focus();
    }
  });

  defineExpose({ textareaRef });
</script>

<template>
  <FormField
    :id="inputId"
    :label="label"
    :optional-label="optionalLabel"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :size="size"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template #default="{ describedBy, hasError }">
      <div :class="fieldWrapperClass">
        <textarea
          :id="inputId"
          ref="textareaRef"
          :class="[textareaClass, isSmall && textareaSmallClass]"
          :style="textareaStyle"
          :data-testid="resolvedTestId"
          :data-invalid="hasError ? '' : undefined"
          :data-disabled="disabled || loading ? '' : undefined"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled || loading"
          :rows="rows"
          :cols="cols"
          :wrap="wrap"
          :maxlength="maxLength"
          :required="required"
          :aria-invalid="hasError ? 'true' : undefined"
          :aria-describedby="describedBy"
          :aria-busy="loading ? 'true' : undefined"
          :value="modelValue"
          @input="onInput"
          @blur="onBlur"
        ></textarea>

        <Loader
          v-if="loading"
          :class="loaderWrapperClass"
          :size="LoaderSize.SM"
          :test-id="`${resolvedTestId}-loader`"
        />
      </div>
      <div
        v-if="hasCounter"
        :class="helperTextClass"
        :data-testid="`${resolvedTestId}-counter`"
        aria-hidden="true"
      >
        {{ counterText }}
      </div>
    </template>
  </FormField>
</template>
